import { FOLIE_MM, MIN_AREA_M2, SILICONEN_PER_M1_CENTS, SPACER_TYPES, findBuildup, paneFolieCount, paneThicknessMm, spacerType, vormSurcharge, type Buildup } from "./isolatieglas-data";
import { modelUg, type Gas } from "@/features/tools/u-value";

/** Gewicht (kg/m²) van één ruit: floatglas ≈ 2,5 kg/m² per mm, PVB-folie ≈ 1,1 kg/m² per mm. */
function paneWeightKgM2(token: string): number {
  const folies = paneFolieCount(token);
  const glassMm = paneThicknessMm(token) - folies * FOLIE_MM;
  return glassMm * 2.5 + folies * FOLIE_MM * 1.1;
}

/** Standaard spouw (mm) als er niets gekozen is; welke maten kunnen hangt van de afstandhouder af. */
export const SPOUW_MM = 15;

// --- Timmers-toeslagen (zelfde leverancier als enkel glas; niet in de isolatieglas-prijslijst) ---
/** Grootte-toeslag over de glasprijs: +50% vanaf 3 m², +100% vanaf 6 m² (mirror enkel glas). */
export const ISO_SIZE_TIERS: { fromM2: number; pct: number }[] = [
  { fromM2: 6, pct: 100 },
  { fromM2: 3, pct: 50 },
];
/**
 * Energietoeslag: cent per kg glasgewicht. Stond op 3,7 ct/kg (overgenomen van enkel glas), maar
 * Timmers rekent voor isolatieglas 10,5 ct/kg — vastgesteld op hun opdrachtbevestiging bij order
 * 1023 (02-09-2026): € 9,20 over 87,6 kg. Met 3,7 ct rekenden wij € 3,30 en liepen we het verschil
 * mis op elke isolatieglasbestelling.
 */
export const ISO_ENERGY_CENTS_PER_KG = 10.5;

/**
 * De toeslagen die Timmers bovenop de m²-prijs rekent. Ze staan NIET in de prijslijst-Excel en
 * komen ook niet uit de API — Timmers zet ze los op de opdrachtbevestiging. Daarom zijn het
 * instellingen: zo kun je een gewijzigd tarief in de admin bijwerken zonder nieuwe deploy, wat
 * precies het probleem was toen ze zeven maanden lang stilletjes verouderden.
 *
 * De waarden hieronder zijn de terugval wanneer de admin (nog) niets heeft ingevuld.
 */
export interface IsoToeslagen {
  /** Energietoeslag in cent per kg glasgewicht. */
  energieCentPerKg: number;
  /** Afstandhouder-toeslag per type: netto inkoop in cent per m1 omtrek. */
  spacerPerM1Cents: Record<string, number>;
}

export const DEFAULT_ISO_TOESLAGEN: IsoToeslagen = {
  energieCentPerKg: ISO_ENERGY_CENTS_PER_KG,
  // Afgeleid van SPACER_TYPES, zodat de tarieven en de keuzelijst niet uit elkaar kunnen lopen.
  spacerPerM1Cents: Object.fromEntries(SPACER_TYPES.map((s) => [s.id, s.perM1Cents])),
};

/**
 * Oppervlakte waarover de energietoeslag gaat: de WERKELIJKE maat, op 2 decimalen — niet de
 * afgeronde maat waarop het glas geprijsd wordt. Timmers rekent de toeslag over het gewicht van
 * het glas dat er daadwerkelijk ligt; de prijs gaat over de plaat die ze moeten snijden. Bij order
 * 1023 is dat 2,19 m² per ruit (niet 2,23), en alleen zo komt hun € 9,20 er exact uit.
 */
export const energieOppM2 = (widthMm: number, heightMm: number): number =>
  Math.round((widthMm / 1000) * (heightMm / 1000) * 100) / 100;
/** Grootte-toeslag-% voor een oppervlak: hoogste tier ≤ het oppervlak. */
export function isoSizeSurchargePct(areaM2: number): number {
  return ISO_SIZE_TIERS.find((t) => areaM2 >= t.fromM2)?.pct ?? 0;
}

// --- U-waardetabel van de leverancier (2-bladig isolatieglas, samenstelling 4-4 mm, argon-spouw).
//     Autoritatief: deze waarden tonen we voor dubbelglas i.p.v. de EN 673-schatting.
export const U_TABLE_SPOUW = [6, 7, 9, 10, 12, 13, 14, 15, 16, 18, 20, 24];
export const U_TABLE: Record<string, number[]> = {
  standaard: [3, 3, 2.8, 2.8, 2.7, 2.7, 2.6, 2.6, 2.6, 2.6, 2.6, 2.6],
  "hr-1.1": [2, 1.8, 1.6, 1.4, 1.3, 1.2, 1.2, 1.1, 1.1, 1.2, 1.2, 1.2], // BASIS
  "hr-1.0": [2, 1.8, 1.5, 1.4, 1.2, 1.1, 1.1, 1.0, 1.0, 1.1, 1.1, 1.1], // PREMIUM (= SUN)
};
/** U-waarde uit de leverancierstabel (dubbelglas); lineair interpoleren voor tussenliggende spouw.
 *  Null voor triple/onbekend → dan valt de engine terug op de EN 673-schatting. */
function tableUValue(pakket: string, spouwMm: number): number | null {
  const row = pakket.startsWith("zonwerend") ? U_TABLE["hr-1.0"] : U_TABLE[pakket];
  if (!row) return null;
  const xs = U_TABLE_SPOUW;
  if (spouwMm <= xs[0]!) return row[0]!;
  if (spouwMm >= xs[xs.length - 1]!) return row[row.length - 1]!;
  for (let i = 0; i < xs.length - 1; i++) {
    if (spouwMm === xs[i]) return row[i]!;
    if (spouwMm > xs[i]! && spouwMm < xs[i + 1]!) {
      const t = (spouwMm - xs[i]!) / (xs[i + 1]! - xs[i]!);
      return Math.round((row[i]! + t * (row[i + 1]! - row[i]!)) * 100) / 100;
    }
  }
  return row[row.length - 1]!;
}

// --- U-waarde-schatting — gebruikt de GEDEELDE EN 673-fysica (zelfde als de U-waarde-calculator).
const REF_SPOUW_MM = 16; // spouw waarbij de HR-rating (1,0 / 1,1) geldt

/** Geschatte U-waarde (EN 673, vereenvoudigd) — reageert op spouwbreedte, gas en coating.
 *  HR (1.0/1.1) verankerd op de rating bij REF_SPOUW; standaard/triple/zonwerend zuiver model. */
export function estimateUValue(opts: { pakket: string; coating: string | null; gas: string; spouwMm: number; isTriple: boolean; ratedU: number | null }): number {
  const lowE = opts.pakket === "hr-1.0" ? 0.025 : 0.04;
  const panes = opts.isTriple ? 3 : 2;
  const coatedCavities = opts.coating === "both" ? (opts.isTriple ? 2 : 1) : opts.coating ? 1 : 0;
  const gas = (opts.gas === "argon" || opts.gas === "krypton" || opts.gas === "lucht" ? opts.gas : "argon") as Gas;
  const u = modelUg({ panes, coatedCavities, gas, widthMm: opts.spouwMm, lowE });
  if (opts.ratedU != null) {
    const uRef = modelUg({ panes, coatedCavities, gas, widthMm: REF_SPOUW_MM, lowE });
    return Math.round((opts.ratedU + (u - uRef)) * 100) / 100;
  }
  return Math.round(u * 100) / 100;
}

export interface IsoConfig {
  pakket: string;
  outer: string;
  inner: string;
  /** Middenruit (alleen triple). */
  middle?: string;
  widthMm: number;
  heightMm: number;
  quantity: number;
  /** Vorm (toeslag als enkel glas); default "rechthoek". */
  shapeId?: string;
  /** Spouwbreedte (mm), kiesbaar 6–24; default SPOUW_MM. Geen prijsinvloed in de lijst. */
  spouwMm?: number;
  /** Spouw-vulling + afstandhouder-kleur — vastgelegd, géén prijsinvloed (niet in de data). */
  gas?: string;
  /** Type afstandhouder ("alu" | "tgi") — bepaalt de spouwmaten én een toeslag per m1 omtrek. */
  spacerColor?: string;
  /** UV-bestendige siliconen randafdichting (los vinkje bovenop de afstandhouder). */
  siliconen?: boolean;
  /** Toeslagtarieven uit de admin; weglaten = DEFAULT_ISO_TOESLAGEN. */
  toeslagen?: IsoToeslagen;
  /** Marge (%) bovenop de netto-inkoop pricePerM2 (admin-instelling isoMarginPct); 0 = inkoop 1:1. */
  marginPct?: number;
}

export interface IsoPrice {
  buildup: Buildup;
  areaM2: number;
  billableAreaM2: number;
  unitCents: number;
  totalCents: number;
  uValue: number | null;
  totalThicknessMm: number;
  /** Gewicht van de hele opbouw (kg/m²) — glasbladen + folies. */
  weightKgM2: number;
  // Prijs-uitsplitsing (alle bedragen ná marge, cents; sommeren tot unitCents):
  /** Verkooptarief per m² (netto-inkoop × (1+marge)). */
  sellPricePerM2Cents: number;
  /** Glas-basisbedrag (oppervlakte × verkooptarief). */
  glassBaseCents: number;
  /** Grootte-toeslag-% (0/50/100) + bedrag. */
  sizeSurchargePct: number;
  sizeCents: number;
  /** Vorm-toeslagbedrag. */
  vormCents: number;
  /** Energietoeslagbedrag (€/kg × glasgewicht). */
  energyCents: number;
  /** Afstandhouder-toeslag (TGI) + siliconen randafdichting, per m1 omtrek. */
  spacerCents: number;
  siliconenCents: number;
}

/** Driemaat: glas wordt op 30mm-stappen afgerekend (mirror van de enkelglas-engine). */
const ceil30 = (mm: number): number => (mm % 30 === 0 ? mm : mm + 30 - (mm % 30));
const billableArea = (w: number, h: number): number => Math.round(((ceil30(w) * ceil30(h)) / 1e6) * 100) / 100;

/** Prijs/U-waarde/dikte voor een isolatieglas-config; null als de combinatie niet bestaat of maten ongeldig zijn. */
export function calcIso(c: IsoConfig): IsoPrice | null {
  const b = findBuildup(c.pakket, c.outer, c.inner, c.middle);
  if (!b) return null;
  if (!(c.widthMm > 0) || !(c.heightMm > 0) || !(c.quantity >= 1)) return null;
  const billableAreaM2 = Math.max(billableArea(c.widthMm, c.heightMm), Math.max(MIN_AREA_M2, b.minAreaM2));
  const margin = Number.isFinite(c.marginPct) && (c.marginPct ?? 0) > 0 ? c.marginPct! : 0;
  const weightKgM2 = Math.round((paneWeightKgM2(c.outer) + paneWeightKgM2(c.inner) + (b.middle ? paneWeightKgM2(b.middle) : 0)) * 10) / 10;

  // Prijsopbouw ná marge (mFactor). Grootte- en vormtoeslag = % over de glasprijs; energietoeslag
  // = €/kg × glasgewicht. Elke post apart afgerond, unit = de som (zodat de Controle klopt).
  const mFactor = 1 + margin / 100;
  const sellPricePerM2Cents = Math.round(b.pricePerM2 * mFactor);
  const glassRawCents = billableAreaM2 * b.pricePerM2; // netto inkoop, vóór marge
  const sizePct = isoSizeSurchargePct(billableAreaM2);
  const vormPct = vormSurcharge(c.shapeId ?? "rechthoek");
  const glassBaseCents = Math.round(glassRawCents * mFactor);
  const sizeCents = Math.round(glassRawCents * (sizePct / 100) * mFactor);
  const vormCents = Math.round(glassRawCents * vormPct * mFactor);
  const tv = c.toeslagen ?? DEFAULT_ISO_TOESLAGEN;
  const energyCents = Math.round(weightKgM2 * energieOppM2(c.widthMm, c.heightMm) * tv.energieCentPerKg * mFactor);
  // Afstandhouder + siliconen randafdichting: netto inkoop per m1 OMTREK. Triple heeft twee
  // spouwen, dus twee afstandhouders en twee randen — daar telt het tarief dubbel.
  const omtrekM = (2 * (c.widthMm + c.heightMm)) / 1000;
  const randen = b.middle ? 2 : 1;
  // Zelfde mFactor als het glas: de afstandhouder krijgt de marge van de opbouw waar hij in zit.
  const spacerTarief = tv.spacerPerM1Cents[c.spacerColor ?? "alu"] ?? spacerType(c.spacerColor).perM1Cents;
  const spacerCents = Math.round(omtrekM * randen * spacerTarief * mFactor);
  // Sinds de siliconen-afwerking een eigen afstandhouder-keuze is ("sil"), zit het tarief al in
  // spacerCents. De losse regel blijft bestaan voor orders van vóór die wijziging.
  const siliconenCents =
    c.siliconen && c.spacerColor !== "sil" ? Math.round(omtrekM * randen * SILICONEN_PER_M1_CENTS * mFactor) : 0;
  const unitCents = glassBaseCents + sizeCents + vormCents + energyCents + spacerCents + siliconenCents;

  const spouw = c.spouwMm ?? SPOUW_MM;
  const totalThicknessMm = Math.round(
    (paneThicknessMm(c.outer) + spouw + paneThicknessMm(c.inner) + (b.middle ? spouw + paneThicknessMm(b.middle) : 0)) * 100,
  ) / 100;
  const gas = c.gas ?? (c.pakket === "standaard" ? "lucht" : "argon");
  return {
    buildup: b,
    areaM2: (c.widthMm / 1000) * (c.heightMm / 1000),
    billableAreaM2,
    unitCents,
    totalCents: unitCents * c.quantity,
    // Dubbelglas: autoritatieve leverancierstabel; triple: EN 673-schatting (tabel is 2-bladig).
    uValue: (b.middle ? null : tableUValue(c.pakket, spouw)) ?? estimateUValue({ pakket: c.pakket, coating: b.coating, gas, spouwMm: spouw, isTriple: !!b.middle, ratedU: b.uValue }),
    totalThicknessMm,
    weightKgM2,
    sellPricePerM2Cents,
    glassBaseCents,
    sizeSurchargePct: sizePct,
    sizeCents,
    vormCents,
    energyCents,
    spacerCents,
    siliconenCents,
  };
}
